import type { JSX } from "react";
// Settings / admin page: manual sync triggers, webhook
// configs. Single page with sections — keeps navigation simple for v1.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { api, type Webhook } from "../api.js";
import { Modal } from "../components/Modal.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Label } from "../components/ui/label.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";
import { NUMBER_FORMATS, useNumberFormat, type NumberFormat } from "../contexts/numberFormat/index.js";
import { THEME_MODES, useTheme, type ThemeMode } from "../contexts/theme/index.js";

const STAT_GRID = "grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4";

const THEME_LABEL: Record<ThemeMode, string> = {
  system: "System (follow OS)",
  light: "Light",
  dark: "Dark",
};

export function Settings(): JSX.Element {
  return (
    <>
      <PageHeader title="Settings" />
      <DisplaySection />
      <SyncSection />
      <WebhooksSection />
    </>
  );
}

// ---------- Display preferences ----------

function DisplaySection(): JSX.Element {
  const { format, setFormat, formatNumber, formatMoney } = useNumberFormat();
  const { mode, setMode, resolved } = useTheme();
  const SAMPLE = 123456789; // 1,234,567.89 in major units
  return (
    <>
      <h3 className="mb-4 text-lg font-semibold">Display preferences</h3>
      <div className={`${STAT_GRID} mb-8`}>
        <Card className="gap-2 p-5">
          <span className="font-medium">Number format</span>
          <p className="text-muted-foreground text-xs">
            Affects every number and money figure across the app.
          </p>
          <Select
            value={format}
            onChange={(e) => {
              setFormat(e.target.value as NumberFormat);
            }}
          >
            {NUMBER_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f === "US" ? "US (1,234.56)" : "EU (1.234,56)"}
              </option>
            ))}
          </Select>
          <p className="text-muted-foreground text-xs">
            Preview: <strong>{formatNumber(SAMPLE / 100)}</strong> ·{" "}
            <strong>{formatMoney(SAMPLE, "EUR")}</strong>
          </p>
        </Card>
        <Card className="gap-2 p-5">
          <span className="font-medium">Theme</span>
          <p className="text-muted-foreground text-xs">
            Pick a colour scheme or follow the operating system.
          </p>
          <Select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value as ThemeMode);
            }}
          >
            {THEME_MODES.map((m) => (
              <option key={m} value={m}>
                {THEME_LABEL[m]}
              </option>
            ))}
          </Select>
          <p className="text-muted-foreground text-xs">
            Currently showing: <strong>{resolved}</strong>
          </p>
        </Card>
      </div>
    </>
  );
}

// ---------- Manual sync ----------

function SyncSection(): JSX.Element {
  const fx = useMutation({ mutationFn: () => api.syncFx() });
  const banks = useMutation({ mutationFn: () => api.syncBanks() });

  // Each mutation has a different result-data shape; the button only needs the
  // common surface, so the data type is erased to `unknown`.
  const Btn = ({
    label,
    m,
    hint,
  }: {
    label: string;
    m: UseMutationResult<unknown, Error, void>;
    hint: string;
  }): JSX.Element => (
    <Card className="gap-2 p-5">
      <span className="font-medium">{label}</span>
      <p className="text-muted-foreground text-xs">{hint}</p>
      <div>
        <Button
          variant="secondary"
          onClick={() => {
            m.mutate();
          }}
          disabled={m.isPending}
        >
          {m.isPending ? "Syncing..." : "Run now"}
        </Button>
      </div>
      {m.isSuccess && <p className="text-good text-xs break-all">{JSON.stringify(m.data)}</p>}
      {m.isError && <p className="text-bad text-xs">{m.error.message}</p>}
    </Card>
  );

  return (
    <>
      <h3 className="mb-4 text-lg font-semibold">Manual sync</h3>
      <div className={`${STAT_GRID} mb-8`}>
        <Btn label="FX rates" m={fx} hint="Pulls latest from Frankfurter (ECB). Cron: daily 06:00 UTC." />
        <Btn label="Bank syncs" m={banks} hint="Revolut, Monobank, Wise. Cron: every 6h." />
        {/* IBKR is push (n8n → /ingest/ibkr); run it from Settings → Integrations, not here. */}
      </div>
    </>
  );
}

// ---------- Webhooks ----------

const WEBHOOK_ENTITIES = [
  "account",
  "transaction",
  "document",
  "budget",
  "retirement_target",
  "retirement_expense_plan_item",
  "category",
];
const WEBHOOK_EVENTS = ["onInsert", "onUpdate", "onDelete"];

function WebhooksSection(): JSX.Element {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Webhook | null>(null);
  const q = useQuery({ queryKey: ["webhooks"], queryFn: () => api.list<Webhook>("/webhooks") });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhooks"] }),
  });

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Webhooks</h3>
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          + Add webhook
        </Button>
      </div>
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Active</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.items.map((w) => (
              <TableRow key={w.id}>
                <TableCell className="font-medium">{w.name}</TableCell>
                <TableCell>{w.entity}</TableCell>
                <TableCell className="text-muted-foreground">{w.events.join(", ")}</TableCell>
                <TableCell className="text-muted-foreground max-w-[240px] truncate">
                  {w.target_url}
                </TableCell>
                <TableCell>{w.is_active ? "yes" : "no"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(w);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete ${w.name}?`)) remove.mutate(w.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {q.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                  No webhooks.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
        }}
        title={editing ? `Edit ${editing.name}` : "New webhook"}
      >
        <WebhookForm
          initial={editing}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ["webhooks"] });
          }}
        />
      </Modal>
    </>
  );
}

function WebhookForm({ initial, onDone }: { initial: Webhook | null; onDone: () => void }): JSX.Element {
  const [name, setName] = useState(initial?.name ?? "");
  const [targetUrl, setTargetUrl] = useState(initial?.target_url ?? "");
  const [entity, setEntity] = useState(initial?.entity ?? "transaction");
  const [events, setEvents] = useState<string[]>(initial?.events ?? ["onInsert"]);
  const [secret, setSecret] = useState("");
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name,
        target_url: targetUrl,
        entity,
        events,
        is_active: isActive,
      };
      if (secret) body.secret = secret;
      return initial ? api.update(`/webhooks/${initial.id}`, body) : api.create("/webhooks", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  const toggleEvent = (ev: string) => {
    setEvents(events.includes(ev) ? events.filter((e) => e !== ev) : [...events, ev]);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Name *">
        <TextInput
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </Field>
      <Field label="Target URL *">
        <TextInput
          required
          type="url"
          value={targetUrl}
          onChange={(e) => {
            setTargetUrl(e.target.value);
          }}
          placeholder="https://hook.example.com/..."
        />
      </Field>
      <Field label="Entity">
        <Select
          value={entity}
          onChange={(e) => {
            setEntity(e.target.value);
          }}
        >
          {WEBHOOK_ENTITIES.map((en) => (
            <option key={en} value={en}>
              {en}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Events">
        <div className="flex flex-wrap gap-3 py-1">
          {WEBHOOK_EVENTS.map((ev) => (
            <Label key={ev} className="font-normal">
              <input
                type="checkbox"
                className="accent-brand size-4"
                checked={events.includes(ev)}
                onChange={() => {
                  toggleEvent(ev);
                }}
              />
              <span>{ev}</span>
            </Label>
          ))}
        </div>
      </Field>
      <Field label={initial ? "Secret (leave blank to keep current)" : "Secret * (≥ 16 chars)"}>
        <TextInput
          value={secret}
          onChange={(e) => {
            setSecret(e.target.value);
          }}
          placeholder="whsec_..."
          {...(initial ? {} : { required: true, minLength: 16 })}
        />
      </Field>
      <Label className="my-2 font-normal">
        <input
          type="checkbox"
          className="accent-brand size-4"
          checked={isActive}
          onChange={(e) => {
            setIsActive(e.target.checked);
          }}
        />
        <span>Active</span>
      </Label>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}
